import React, { useState, useEffect } from "react";
import { adminService } from "../../services/admin";
import { Button } from "../../components/ui/Button";
import { Modal, Input, Select } from "../../components/ui/Modal";
import { formatUSD, formatPnL, pnlClass } from "../../utils/formatters";
import { usePrices } from "../../hooks/usePrices";
import LivePrice from "../../components/ui/LivePrice";
import OrderBook from "../../components/features/trades/OrderBook";
import Pagination from "../../components/ui/Pagination";
import toast from "react-hot-toast";
import { Ico } from "../../components/ui/Icon";

type ModalType = "arb" | "close" | "edit" | "detail" | "delete" | null;
const PER_PAGE = 20;

const COIN_COLORS: Record<string, string> = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  XRP: "#00AAE4",
  SOL: "#9945FF",
  BNB: "#F3BA2F",
  ADA: "#0033AD",
  DOGE: "#C2A633",
  DOT: "#E6007A",
};

const CloseReason: React.FC<{ reason?: string | null }> = ({ reason }) => {
  const map: Record<string, { t: string; c: string; bg: string }> = {
    stop_loss: { t: "SL", c: "text-red-400", bg: "bg-red-500/10" },
    take_profit: { t: "TP", c: "text-primary", bg: "bg-primary/10" },
    duration: { t: "Timer", c: "text-gray-400", bg: "bg-gray-800/60" },
    force: { t: "Force", c: "text-orange-400", bg: "bg-orange-500/10" },
    manual: { t: "Manual", c: "text-gray-500", bg: "bg-gray-800/40" },
  };
  const m = reason ? map[reason] : map["manual"];
  return m ? (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.bg} ${m.c}`}
    >
      {m.t}
    </span>
  ) : (
    <span className="text-gray-700 text-[10px]">—</span>
  );
};

const AdminTrades: React.FC = () => {
  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [modal, setModal] = useState<ModalType>(null);
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [arbPct, setArbPct] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editLeverage, setEditLeverage] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editPnl, setEditPnl] = useState("");

  const { prices, connected } = usePrices() as any;

  useEffect(() => {
    setPage(1);
  }, [filter, search]);
  useEffect(() => {
    load(page);
  }, [page, filter, search]);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const params: any = { page: p, per_page: PER_PAGE };
      if (filter !== "all") params.status = filter;
      if (search) params.search = search;
      const res = await adminService.getAdminTrades(params);
      setTrades(res.data.data || []);
      setMeta({
        current_page: res.data.current_page,
        last_page: res.data.last_page,
        total: res.data.total,
        from: res.data.from,
        to: res.data.to,
      });
    } catch {
      toast.error("Failed to load trades");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (trade: any, m: ModalType, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelected(trade);
    setModal(m);
    setArbPct("");
    if (m === "edit") {
      setEditAmount(String(trade.amount ?? ""));
      setEditLeverage(String(trade.leverage ?? "1"));
      setEditStatus(trade.status ?? "open");
      setEditPnl(String(trade.profit_loss ?? "0"));
    }
  };

  const forceClose = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await adminService.forceCloseTrade(selected.id);
      toast.success(`Trade #${selected.id} force-closed`);
      setModal(null);
      load(page);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyArb = async () => {
    if (!selected || !arbPct) return;
    setActionLoading(true);
    try {
      await adminService.applyArbitrage(selected.id, Number(arbPct));
      const credit = (
        (Number(arbPct) / 100) *
        parseFloat(String(selected.amount || 0))
      ).toFixed(2);
      toast.success(`+${arbPct}% (+$${credit}) applied to #${selected.id}`);
      setModal(null);
      setArbPct("");
      load(page);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteTrade = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await (adminService as any).deleteTrade(selected.id);
      toast.success(`Trade #${selected.id} deleted`);
      setModal(null);
      load(page);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await (adminService as any).updateTrade?.(selected.id, {
        profit_loss: parseFloat(editPnl),
        status: editStatus,
        amount: parseFloat(editAmount),
        leverage: parseInt(editLeverage),
      });
      toast.success(`Trade #${selected.id} updated`);
      setModal(null);
      load(page);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Update failed");
    } finally {
      setActionLoading(false);
    }
  };

  const calcLivePnl = (trade: any) => {
    const markPx = prices[trade.cryptocurrency] || 0;
    const entry = parseFloat(String(trade.entry_price || 0));
    const amount = parseFloat(String(trade.amount || 0));
    const lev = Number(trade.leverage) || 1;
    const isBuy = trade.direction === "buy";
    if (trade.status !== "open" || entry <= 0 || markPx <= 0)
      return parseFloat(String(trade.profit_loss || 0));
    return ((isBuy ? markPx - entry : entry - markPx) / entry) * amount * lev;
  };

  const totalSize = trades
    .filter((t) => t.status === "open")
    .reduce((s, t) => s + parseFloat(String(t.amount || 0)), 0);
  const totalPnl = trades
    .filter((t) => t.status === "open")
    .reduce((s, t) => s + calcLivePnl(t), 0);

  return (
    <div className="flex flex-col bg-[#080808] min-h-full">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800/60 bg-[#070707]">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-white font-display font-bold">
              Trade Management
            </h2>
            <p className="text-gray-600 text-[10px] mt-0.5">
              Click any row for full analysis
            </p>
          </div>
          <span
            className={`hidden sm:flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-full border ${
              connected !== false
                ? "bg-primary/10 text-primary border-primary/20"
                : "bg-gray-800 text-gray-500 border-gray-700"
            }`}
          >
            <span
              className={`w-1 h-1 rounded-full ${connected !== false ? "bg-primary animate-pulse" : "bg-gray-600"}`}
            />
            {connected !== false ? "LIVE" : "REST"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {filter === "open" && trades.length > 0 && (
            <>
              <div className="hidden md:block text-right">
                <p className="text-[9px] text-gray-600">Open Size</p>
                <p className="text-primary text-xs font-mono font-bold">
                  {formatUSD(totalSize)}
                </p>
              </div>
              <div className="hidden md:block text-right">
                <p className="text-[9px] text-gray-600">Total PnL</p>
                <p
                  className={`text-xs font-mono font-bold ${pnlClass(totalPnl)}`}
                >
                  {formatPnL(totalPnl)}
                </p>
              </div>
            </>
          )}
          <button
            onClick={() => load(page)}
            className="p-2 rounded-xl bg-gray-900 border border-gray-800 text-gray-500 hover:text-white hover:border-gray-700 transition-all"
          >
            <Ico name="refresh" className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-gray-800/50 bg-[#060606] items-center">
        <div className="flex gap-0.5 bg-gray-900/60 p-0.5 rounded-xl border border-gray-800/60">
          {["open", "closed", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-[10px] text-[11px] font-display font-bold capitalize transition-all ${
                filter === f
                  ? "bg-primary text-black"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Ico
            name="search"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 w-3.5 h-3.5 pointer-events-none"
          />
          <input
            className="w-full bg-gray-900/60 border border-gray-800 focus:border-gray-600 rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder-gray-700 outline-none transition-colors"
            placeholder="Search user or coin..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {meta.total > 0 && (
          <span className="ml-auto text-[10px] text-gray-600 font-mono bg-gray-900/60 px-2.5 py-1 rounded-lg border border-gray-800/60">
            {meta.total} trades
          </span>
        )}
      </div>

      {/* ── Table ── */}
      <div className="flex-1 overflow-x-auto">
        {/* Tighter min-width + compact columns reduce cramping on medium screens */}
        <div className="min-w-[780px]">
          <div className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1.1fr_80px_120px] gap-1.5 px-3 py-2.5 border-b border-gray-800/50 bg-[#070707]">
            {[
              "Symbol",
              "User",
              "Size",
              "Entry",
              "Mark",
              "P&L",
              "Status",
              "Actions",
            ].map((h) => (
              <span
                key={h}
                className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
              >
                {h}
              </span>
            ))}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
            </div>
          )}

          {!loading && trades.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-14 h-14 rounded-2xl bg-gray-900 border border-gray-800/60 flex items-center justify-center mb-4">
                <Ico name="chart" className="w-6 h-6 text-gray-700" />
              </div>
              <p className="text-gray-600 text-sm">No {filter} trades found</p>
            </div>
          )}

          <div className="divide-y divide-gray-800/30">
            {trades.map((trade) => {
              const isBuy = trade.direction === "buy";
              const isOpen = trade.status === "open";
              const markPrice = prices[trade.cryptocurrency] || 0;
              const entry = parseFloat(String(trade.entry_price || 0));
              const pnl = calcLivePnl(trade);
              const pnlPct =
                entry > 0 && markPrice > 0 && isOpen
                  ? ((markPrice - entry) / entry) *
                    100 *
                    (isBuy ? 1 : -1) *
                    trade.leverage
                  : null;

              return (
                <div
                  key={trade.id}
                  onClick={() => openModal(trade, "detail")}
                  className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1.1fr_80px_120px] gap-1.5 px-3 py-2.5 items-center hover:bg-white/[0.025] transition-colors cursor-pointer group"
                >
                  {/* Symbol */}
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-black shrink-0"
                      style={{
                        background: `${COIN_COLORS[trade.cryptocurrency] || "#555"}18`,
                        color: COIN_COLORS[trade.cryptocurrency] || "#888",
                      }}
                    >
                      {trade.cryptocurrency?.[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-mono font-bold truncate">
                        {trade.cryptocurrency}/USDT
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span
                          className={`text-[8px] font-bold px-1 py-0.5 rounded ${isBuy ? "bg-primary/15 text-primary" : "bg-red-500/15 text-red-400"}`}
                        >
                          {isBuy ? "▲ L" : "▼ S"}
                        </span>
                        <span className="text-yellow-400/70 text-[8px] font-mono">
                          {trade.leverage}×
                        </span>
                        <span
                          className={`text-[8px] ${trade.wallet_type === "real" ? "text-primary/50" : "text-blue-400/50"}`}
                        >
                          {trade.wallet_type?.[0]?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* User */}
                  <div className="min-w-0">
                    <p className="text-gray-300 text-[11px] font-semibold truncate">
                      {trade.user?.first_name} {trade.user?.last_name}
                    </p>
                    <p className="text-gray-600 text-[10px] truncate">
                      @{trade.user?.username}
                    </p>
                  </div>

                  {/* Size */}
                  <p className="text-white text-[11px] font-mono font-semibold">
                    {formatUSD(trade.amount)}
                  </p>

                  {/* Entry */}
                  <p className="text-gray-300 text-[11px] font-mono">
                    {entry > 0
                      ? `$${entry.toLocaleString(undefined, { maximumFractionDigits: entry < 1 ? 4 : 2 })}`
                      : "—"}
                  </p>

                  {/* Mark Price */}
                  {isOpen && markPrice > 0 ? (
                    <LivePrice
                      price={markPrice}
                      className="text-[11px] font-semibold"
                      baseColor="text-gray-300"
                      showArrow
                    />
                  ) : (
                    <span className="text-gray-700 text-[11px] font-mono">
                      —
                    </span>
                  )}

                  {/* P&L */}
                  <div>
                    <p
                      className={`text-[11px] font-mono font-bold ${pnlClass(pnl)}`}
                    >
                      {formatPnL(pnl)}
                    </p>
                    {pnlPct !== null ? (
                      <p className={`text-[9px] font-mono ${pnlClass(pnlPct)}`}>
                        {pnlPct >= 0 ? "+" : ""}
                        {pnlPct.toFixed(2)}%
                      </p>
                    ) : (
                      <CloseReason reason={trade.close_reason} />
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        trade.status === "open"
                          ? "bg-primary/15 text-primary"
                          : trade.status === "closed"
                            ? "bg-gray-800 text-gray-400"
                            : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {trade.status}
                    </span>
                  </div>

                  {/* Actions */}
                  <div
                    className="flex items-center gap-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => openModal(trade, "edit", e)}
                      title="Edit"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-500/15 border border-blue-500/25 text-blue-400 hover:bg-blue-500/30 transition-all"
                    >
                      <Ico name="save" className="w-3 h-3" />
                    </button>
                    {isOpen && (
                      <button
                        onClick={(e) => openModal(trade, "arb", e)}
                        title="Arbitrage"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-yellow-500/15 border border-yellow-500/25 text-yellow-400 hover:bg-yellow-500/30 transition-all"
                      >
                        <Ico name="flash" className="w-3 h-3" />
                      </button>
                    )}
                    {isOpen && (
                      <button
                        onClick={(e) => openModal(trade, "close", e)}
                        title="Force close"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500/15 border border-red-500/25 text-red-400 hover:bg-red-500/30 transition-all"
                      >
                        <Ico name="close" className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      onClick={(e) => openModal(trade, "delete", e)}
                      title="Delete"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-800/60 border border-gray-700/50 text-gray-500 hover:bg-red-500/15 hover:border-red-500/25 hover:text-red-400 transition-all"
                    >
                      <Ico name="delete" className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Pagination ── */}
      <div className="border-t border-gray-800/50 bg-[#060606]">
        <Pagination
          currentPage={meta.current_page}
          lastPage={meta.last_page}
          total={meta.total}
          from={meta.from}
          to={meta.to}
          onPageChange={(p) => setPage(p)}
          loading={loading}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════════════ */}

      {/* Detail + Analysis */}
      <Modal
        isOpen={modal === "detail"}
        onClose={() => setModal(null)}
        title=""
        size="xl"
      >
        {selected &&
          (() => {
            const isBuy = selected.direction === "buy";
            const isOpen = selected.status === "open";
            const entry = parseFloat(String(selected.entry_price || 0));
            const closePx = parseFloat(String(selected.close_price || 0));
            const markPx = prices[selected.cryptocurrency] || 0;
            const amount = parseFloat(String(selected.amount || 0));
            const leverage = Number(selected.leverage) || 1;
            const sl = selected.stop_loss
              ? parseFloat(String(selected.stop_loss))
              : null;
            const tp = selected.take_profit
              ? parseFloat(String(selected.take_profit))
              : null;
            const exitPx = isOpen ? markPx : closePx > 0 ? closePx : 0;
            const rawPnl = parseFloat(String(selected.profit_loss || 0));
            const livePnl =
              isOpen && entry > 0 && markPx > 0
                ? ((isBuy ? markPx - entry : entry - markPx) / entry) *
                  amount *
                  leverage
                : rawPnl;
            const pnl = isOpen ? livePnl : rawPnl;
            const pnlPct = amount > 0 ? (pnl / amount) * 100 : 0;
            const diff = entry > 0 && exitPx > 0 ? exitPx - entry : null;
            const movePct =
              diff !== null && entry > 0 ? (diff / entry) * 100 : null;
            const isProfit = pnl >= 0;
            const isDirProfit = isBuy ? (diff ?? 0) >= 0 : (diff ?? 0) <= 0;
            const coinColor = COIN_COLORS[selected.cryptocurrency] || "#888";
            const notional = amount * leverage;
            const openedTime =
              (selected.opened_at || selected.created_at)
                ?.slice(0, 16)
                ?.replace("T", " ") || "—";

            return (
              <div className="flex flex-col -m-5 overflow-hidden rounded-2xl">
                {/* Hero banner */}
                <div
                  className={`relative px-5 pt-5 pb-4 shrink-0 ${isProfit ? "bg-gradient-to-br from-emerald-950 via-[#0d1f14] to-[#0a1a10]" : "bg-gradient-to-br from-red-950 via-[#1f0d0d] to-[#1a0a0a]"}`}
                >
                  <div className="flex max-sm:flex-col items-start gap-3 pr-8">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-black shrink-0"
                      style={{
                        background: `${coinColor}25`,
                        color: coinColor,
                        border: `2px solid ${coinColor}40`,
                      }}
                    >
                      {selected.cryptocurrency?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-mono font-black text-lg leading-tight">
                        {selected.cryptocurrency}/USDT
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span
                          className={`text-[9px] font-black px-2 py-0.5 rounded-lg ${isBuy ? "bg-emerald-500/30 text-emerald-300 border border-emerald-500/40" : "bg-red-500/30 text-red-300 border border-red-500/40"}`}
                        >
                          {isBuy ? "▲ LONG" : "▼ SHORT"}
                        </span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-lg bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">
                          {leverage}×
                        </span>
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded-lg ${selected.wallet_type === "real" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-blue-500/20 text-blue-300 border border-blue-500/30"}`}
                        >
                          {selected.wallet_type?.toUpperCase()}
                        </span>
                        <span className="text-white/40 text-[9px] font-mono truncate">
                          @{selected.user?.username} · #{selected.id}
                        </span>
                      </div>
                    </div>
                    <div className="text-right max-sm:text-left shrink-0">
                      <p
                        className={`text-2xl font-mono font-black leading-tight ${isProfit ? "text-emerald-300" : "text-red-400"}`}
                      >
                        {formatPnL(pnl)}
                      </p>
                      <p
                        className={`text-sm font-mono mt-0.5 ${isProfit ? "text-emerald-400" : "text-red-500"}`}
                      >
                        {pnlPct >= 0 ? "+" : ""}
                        {pnlPct.toFixed(2)}% ROI
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body: flex-col on mobile, flex-row on xl — scrollable */}
                <div
                  className="flex flex-col xl:flex-row bg-[#0e0e0e] overflow-y-auto"
                  style={{ maxHeight: "65vh" }}
                >
                  {/* Analysis */}
                  <div className="flex-1 p-4 flex flex-col gap-3 min-w-0 overflow-y-auto">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2.5 bg-[#1a1a1a] border border-white/10 rounded-xl">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">
                          Size
                        </p>
                        <p className="text-white text-sm font-mono font-bold truncate">
                          {formatUSD(amount)}
                        </p>
                        <p className="text-gray-500 text-[9px] font-mono mt-0.5 truncate">
                          {formatUSD(notional)} notional
                        </p>
                      </div>
                      <div className="p-2.5 bg-[#1a1a1a] border border-white/10 rounded-xl">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">
                          Entry
                        </p>
                        <p className="text-gray-100 text-sm font-mono font-bold truncate">
                          {entry > 0
                            ? `$${entry.toLocaleString(undefined, { maximumFractionDigits: entry < 1 ? 5 : 2 })}`
                            : "—"}
                        </p>
                        <p className="text-gray-600 text-[9px] font-mono mt-0.5 truncate">
                          {openedTime}
                        </p>
                      </div>
                      <div className="p-2.5 bg-[#1a1a1a] border border-white/10 rounded-xl">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">
                          {isOpen ? "Mark" : "Exit"}
                        </p>
                        {isOpen && markPx > 0 ? (
                          <>
                            <LivePrice
                              price={markPx}
                              className="text-sm font-bold text-emerald-300"
                              baseColor="text-emerald-300"
                              showArrow
                            />
                            {movePct !== null && (
                              <p
                                className={`text-[9px] font-mono mt-0.5 ${movePct >= 0 ? "text-emerald-400" : "text-red-400"}`}
                              >
                                {movePct >= 0 ? "+" : ""}
                                {movePct.toFixed(2)}%
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-100 text-sm font-mono font-bold truncate">
                            {exitPx > 0
                              ? `$${exitPx.toLocaleString(undefined, { maximumFractionDigits: exitPx < 1 ? 5 : 2 })}`
                              : "—"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Price movement bar */}
                    {diff !== null && entry > 0 && exitPx > 0 && (
                      <div className="p-3 bg-[#1a1a1a] border border-white/10 rounded-xl">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                          <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">
                            Price Movement
                          </span>
                          <span
                            className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${isDirProfit ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-400 border border-red-500/30"}`}
                          >
                            {isDirProfit ? "✓ Profitable" : "✗ Against"}
                          </span>
                        </div>
                        <div className="relative h-4 bg-gray-800/80 rounded-full overflow-hidden mb-1.5">
                          <div
                            className={`absolute inset-y-0 left-0 rounded-full ${isDirProfit ? "bg-gradient-to-r from-emerald-600 to-emerald-400" : "bg-gradient-to-r from-red-700 to-red-500"}`}
                            style={{
                              width: `${Math.min(Math.abs(movePct ?? 0) * 10, 96)}%`,
                              opacity: 0.85,
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-[9px] font-mono gap-1">
                          <span className="text-gray-500 truncate">
                            $
                            {entry.toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          <span
                            className={`font-bold ${diff >= 0 ? "text-emerald-300" : "text-red-400"}`}
                          >
                            {diff >= 0 ? "+" : ""}$
                            {Math.abs(diff).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}{" "}
                            ({movePct?.toFixed(2)}%)
                          </span>
                          <span className="text-gray-500 truncate">
                            $
                            {exitPx.toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* SL / TP */}
                    <div className="grid grid-cols-2 gap-2">
                      {sl ? (
                        <div className="p-2.5 bg-red-950/40 border border-red-500/30 rounded-xl">
                          <p className="text-[9px] text-red-400 uppercase tracking-wide font-bold mb-1">
                            🛑 Stop Loss
                          </p>
                          <p className="text-red-300 font-mono font-black text-base">
                            $
                            {sl.toLocaleString(undefined, {
                              maximumFractionDigits: 4,
                            })}
                          </p>
                          {entry > 0 && (
                            <p className="text-red-500/60 text-[9px] font-mono mt-0.5">
                              {((Math.abs(sl - entry) / entry) * 100).toFixed(
                                2,
                              )}
                              % from entry
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="p-2.5 bg-[#1a1a1a] border border-white/5 rounded-xl flex items-center justify-center">
                          <p className="text-gray-700 text-xs">No SL</p>
                        </div>
                      )}
                      {tp ? (
                        <div className="p-2.5 bg-emerald-950/40 border border-emerald-500/30 rounded-xl">
                          <p className="text-[9px] text-emerald-400 uppercase tracking-wide font-bold mb-1">
                            ✅ Take Profit
                          </p>
                          <p className="text-emerald-300 font-mono font-black text-base">
                            $
                            {tp.toLocaleString(undefined, {
                              maximumFractionDigits: 4,
                            })}
                          </p>
                          {entry > 0 && (
                            <p className="text-emerald-600/60 text-[9px] font-mono mt-0.5">
                              {((Math.abs(tp - entry) / entry) * 100).toFixed(
                                2,
                              )}
                              % from entry
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="p-2.5 bg-[#1a1a1a] border border-white/5 rounded-xl flex items-center justify-center">
                          <p className="text-gray-700 text-xs">No TP</p>
                        </div>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        {
                          l: "Status",
                          v: selected.status,
                          c: isOpen ? "text-emerald-300" : "text-gray-300",
                        },
                        {
                          l: "Closed By",
                          v:
                            selected.close_reason ||
                            (isOpen ? "Open" : "Manual"),
                          c: "text-gray-300",
                        },
                        { l: "Opened", v: openedTime, c: "text-gray-300" },
                        {
                          l: "Closed At",
                          v:
                            selected.closed_at
                              ?.slice(0, 16)
                              ?.replace("T", " ") || "—",
                          c: "text-gray-300",
                        },
                      ].map((item) => (
                        <div
                          key={item.l}
                          className="p-2 bg-[#1a1a1a] border border-white/5 rounded-lg"
                        >
                          <p className="text-[9px] text-gray-600 uppercase tracking-wide">
                            {item.l}
                          </p>
                          <p
                            className={`text-xs font-mono font-semibold mt-0.5 capitalize ${item.c}`}
                          >
                            {item.v}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Admin action buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setModal("edit")}
                        className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold hover:bg-blue-500/20 transition-all"
                      >
                        <Ico name="save" className="w-3.5 h-3.5" /> Edit
                      </button>
                      {isOpen && (
                        <>
                          <button
                            onClick={() => setModal("arb")}
                            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs font-bold hover:bg-yellow-500/20 transition-all"
                          >
                            <Ico name="flash" className="w-3.5 h-3.5" /> Arb
                          </button>
                          <button
                            onClick={() => setModal("close")}
                            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold hover:bg-red-500/20 transition-all"
                          >
                            <Ico name="close" className="w-3.5 h-3.5" /> Force
                            Close
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setModal("delete")}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gray-800/60 border border-gray-700/50 text-gray-500 text-xs font-bold hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-400 transition-all"
                      >
                        <Ico name="delete" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Order Book — full-width collapsed section on mobile, sidebar on xl */}
                  <div className="xl:w-[195px] xl:shrink-0 border-t xl:border-t-0 xl:border-l border-white/[0.06] flex flex-col">
                    <div className="px-3 pt-2.5 pb-1 border-b border-white/[0.06] shrink-0">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        {selected.cryptocurrency}/USDT · Order Book
                      </p>
                    </div>
                    {/* Fixed height on mobile so it doesn't overflow */}
                    <div className="h-[260px] xl:h-full xl:flex-1 overflow-y-auto">
                      <OrderBook symbol={selected.cryptocurrency} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
      </Modal>

      {/* Edit */}
      <Modal
        isOpen={modal === "edit"}
        onClose={() => setModal(null)}
        title={`Edit Trade #${selected?.id}`}
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-900/60 rounded-xl border border-gray-800/60">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shrink-0"
              style={{
                background: `${COIN_COLORS[selected?.cryptocurrency] || "#555"}18`,
                color: COIN_COLORS[selected?.cryptocurrency] || "#aaa",
              }}
            >
              {selected?.cryptocurrency?.[0]}
            </div>
            <div>
              <p className="text-white font-mono font-bold">
                {selected?.cryptocurrency}/USDT — #{selected?.id}
              </p>
              <p className="text-gray-500 text-xs">
                @{selected?.user?.username}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Amount (USD)"
              type="number"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
            />
            <Input
              label="Leverage"
              type="number"
              min="1"
              max="1000"
              suffix={<span className="text-gray-400 text-xs">×</span>}
              value={editLeverage}
              onChange={(e) => setEditLeverage(e.target.value)}
            />
          </div>
          <Input
            label="Profit / Loss (USD)"
            type="number"
            step="0.01"
            placeholder="e.g. 120.00 or -45.00"
            value={editPnl}
            onChange={(e) => setEditPnl(e.target.value)}
          />
          <Select
            label="Status"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
            options={[
              { value: "open", label: "Open" },
              { value: "closed", label: "Closed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-yellow-300 text-xs">
            ⚠ Use Arbitrage to add % profit to open trades — it's more precise.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              fullWidth
              loading={actionLoading}
              onClick={saveEdit}
              icon={<Ico name="save" className="w-3.5 h-3.5" />}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Arbitrage */}
      <Modal
        isOpen={modal === "arb"}
        onClose={() => setModal(null)}
        title="Apply Arbitrage Profit"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-900/60 rounded-xl border border-gray-800/60">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shrink-0"
              style={{
                background: `${COIN_COLORS[selected?.cryptocurrency] || "#555"}18`,
                color: COIN_COLORS[selected?.cryptocurrency] || "#aaa",
              }}
            >
              {selected?.cryptocurrency?.[0]}
            </div>
            <div>
              <p className="text-white font-mono font-bold">
                {selected?.cryptocurrency}/USDT — #{selected?.id}
              </p>
              <p className="text-gray-500 text-xs">
                Size:{" "}
                <span className="text-primary font-mono">
                  {formatUSD(selected?.amount || 0)}
                </span>
              </p>
            </div>
          </div>
          <Input
            label="Profit % to Credit"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="e.g. 2.5"
            suffix={<span className="text-gray-400">%</span>}
            value={arbPct}
            onChange={(e) => setArbPct(e.target.value)}
          />
          {arbPct && Number(arbPct) > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl">
              <span className="text-gray-500 text-sm">Will credit</span>
              <span className="text-primary font-mono font-black text-2xl">
                +$
                {(
                  (Number(arbPct) / 100) *
                  parseFloat(String(selected?.amount || 0))
                ).toFixed(2)}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              fullWidth
              loading={actionLoading}
              onClick={applyArb}
              disabled={!arbPct || Number(arbPct) <= 0}
              icon={<Ico name="flash" className="w-3.5 h-3.5" />}
            >
              Apply +{arbPct || "0"}%
            </Button>
          </div>
        </div>
      </Modal>

      {/* Force Close */}
      <Modal
        isOpen={modal === "close"}
        onClose={() => setModal(null)}
        title="Force Close Trade"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-900/60 rounded-xl border border-gray-800/60">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shrink-0"
              style={{
                background: `${COIN_COLORS[selected?.cryptocurrency] || "#555"}18`,
                color: COIN_COLORS[selected?.cryptocurrency] || "#aaa",
              }}
            >
              {selected?.cryptocurrency?.[0]}
            </div>
            <div className="flex-1">
              <p className="text-white font-mono font-bold">
                {selected?.cryptocurrency}/USDT — #{selected?.id}
              </p>
              <p className="text-gray-500 text-xs">
                @{selected?.user?.username} · {formatUSD(selected?.amount || 0)}
              </p>
            </div>
            {prices[selected?.cryptocurrency] > 0 && (
              <div className="text-right">
                <p className="text-[9px] text-gray-600">Mark</p>
                <LivePrice
                  price={prices[selected?.cryptocurrency]}
                  className="text-sm font-bold"
                  baseColor="text-white"
                  showArrow
                />
              </div>
            )}
          </div>
          <p className="text-gray-400 text-sm">
            Settles P&L immediately at current market price.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={actionLoading}
              onClick={forceClose}
              icon={<Ico name="close" className="w-3.5 h-3.5" />}
            >
              Force Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete */}
      <Modal
        isOpen={modal === "delete"}
        onClose={() => setModal(null)}
        title="Delete Trade"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
            <p className="text-red-300 font-semibold mb-1.5">
              ⚠ This cannot be undone
            </p>
            <p className="text-gray-400 text-sm">
              Permanently delete trade{" "}
              <strong className="text-white">#{selected?.id}</strong> (
              {selected?.cryptocurrency} · {formatUSD(selected?.amount || 0)}).
              The user's wallet will NOT be refunded.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={actionLoading}
              onClick={deleteTrade}
              icon={<Ico name="delete" className="w-3.5 h-3.5" />}
            >
              Delete Forever
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminTrades;
